import { useEffect, useState, useCallback } from "react";
import { formatCurrency } from "../../../utilities/formatCurrency";
import { useParams, useNavigate } from "react-router-dom";
import {
  fetchProduct,
  fetchProductOptionList,
  fetchProductOptions,
} from "../../../lib/webService/webService";
import PropTypes from "prop-types";
import { ArrowLeft, Pencil, ShieldExclamation, BoxSeam } from "react-bootstrap-icons";
import { useMerchantAuth, useMultiAuthCheck } from "../../../utilities/authCheck";
import { Seo } from "../../../components/Seo";
import { generateNoIndexSEO } from "../../../lib/seo/metaGenerators";

if (typeof document !== "undefined" && !document.getElementById("pp-styles")) {
  const s = document.createElement("style");
  s.id = "pp-styles";
  s.textContent = `
    @keyframes _pp_sk { 0%,100%{opacity:1}50%{opacity:.4} }
    ._pp_sk { animation: _pp_sk 1.8s ease-in-out infinite; background: rgba(60,60,67,0.08); }
    .pp-icon-btn { transition: background .15s ease !important; }
    .pp-icon-btn:hover { filter: brightness(0.92); }
    .pp-container { max-width: 900px; margin: 0 auto; padding: 28px 16px 48px; }
    .pp-main-grid { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 2fr); gap: 16px; margin-bottom: 24px; }
    .pp-image { width: 100%; height: 260px; object-fit: contain; display: block; border-radius: 12px; padding: 24px; box-sizing: border-box; }
    .pp-cat-type-grid { display: grid; grid-template-columns: 1fr; gap: 12px; }
    .pp-cat-type-grid.pp-two { grid-template-columns: 1fr 1fr; }
    @media (max-width: 768px) {
      .pp-main-grid { grid-template-columns: 1fr; }
      .pp-image { height: 200px; padding: 16px; }
    }
    @media (max-width: 480px) {
      .pp-container { padding: 20px 12px 40px; }
      .pp-cat-type-grid.pp-two { grid-template-columns: 1fr; }
    }
  `;
  document.head.appendChild(s);
}

const FONT = '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Arial, sans-serif';
const PAGE = { minHeight: "100vh", backgroundColor: "#F2F2F7", fontFamily: FONT };

const Skel = ({ w, h, r = 8, style = {} }) => (
  <div className="_pp_sk" style={{ width: w, height: h, borderRadius: r, ...style }} />
);

const card = {
  backgroundColor: "#fff",
  borderRadius: 16,
  border: "1px solid rgba(60,60,67,.09)",
  padding: "18px 20px",
  boxShadow: "0 1px 3px rgba(0,0,0,.04)",
};

// ─── OpList ───────────────────────────────────────────────────────────────────

const OpList = ({ id }) => {
  const { productId } = useParams();
  const [opArr,   setOpArr]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        setLoading(true);
        const data = await fetchProductOptionList(productId, id);
        setOpArr(data);
        setError(null);
      } catch (error) {
        console.error(error);
        setError("Failed to load options");
      } finally {
        setLoading(false);
      }
    };
    fetchOptions();
  }, [productId, id]);

  if (loading) {
    return (
      <div style={{ display: "flex", gap: 8, paddingTop: 8, flexWrap: "wrap" }}>
        {[70, 52, 88, 62].map((w, i) => <Skel key={i} w={w} h={26} r={13} />)}
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ backgroundColor: "rgba(255,59,48,.08)", borderRadius: 8, padding: "8px 12px", marginTop: 8, fontSize: "0.8125rem", color: "#FF3B30" }}>
        {error}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, paddingTop: 8 }}>
      {opArr.map((item) => (
        <span
          key={item.id}
          style={{
            padding: "4px 12px", borderRadius: 20,
            backgroundColor: "rgba(60,60,67,.07)",
            color: "rgba(60,60,67,.65)",
            fontSize: "0.8125rem", fontWeight: 400,
          }}
        >
          {item?.value}
        </span>
      ))}
    </div>
  );
};

OpList.propTypes = {
  id: PropTypes.string.isRequired,
};

// ─── Product ──────────────────────────────────────────────────────────────────

const Product = () => {
  const navigate = useNavigate();
  const { storeId, productId } = useParams();
  const { isAuthorized, isLoading: authLoading, logout } = useMultiAuthCheck(["elite", "merchant"]);

  const [state, setState] = useState({
    product: null,
    options: [],
    loading: true,
    error: null,
  });

  const fetchProductData = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      const [productData, optionsData] = await Promise.all([
        fetchProduct(productId),
        fetchProductOptions(productId),
      ]);
      setState((prev) => ({ ...prev, product: productData, options: optionsData, loading: false, error: null }));
    } catch (error) {
      console.error(error);
      setState((prev) => ({ ...prev, loading: false, error: "Failed to load product details" }));
    }
  }, [productId]);

  useEffect(() => { fetchProductData(); }, [fetchProductData]);

  const handleAddBtnClick = useCallback(() => {
    navigate(`/store/catalog/${storeId}/${productId}/options/add`);
  }, [navigate, storeId, productId]);

  const handleProductEditRedirect = useCallback((e) => {
    e.preventDefault();
    navigate(`/store/catalog/${storeId}/${productId}/edit`, { state: { ...state.product } });
  }, [navigate, storeId, productId, state.product]);

  const handleOptionEditRedirect = useCallback((e, id, name, title, required) => {
    e.preventDefault();
    navigate(`/store/catalog/${storeId}/${productId}/options/${id}/edit`, { state: { id, name, title, required } });
  }, [navigate, storeId, productId]);

  // ── Auth loading ──
  if (authLoading) {
    return (
      <div style={PAGE}>
        <div className="pp-container" style={{ padding: "28px 16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
            <Skel w={34} h={34} r={17} /><Skel w={150} h={22} r={7} />
          </div>
          {/* 2-col skeleton */}
          <div className="pp-main-grid" style={{ marginBottom: 16 }}>
            <Skel w="100%" h={280} r={16} />
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <Skel w="100%" h={140} r={16} />
              <Skel w="100%" h={100} r={16} />
              <Skel w="100%" h={72} r={16} />
            </div>
          </div>
          <Skel w="100%" h={180} r={16} />
        </div>
      </div>
    );
  }

  // ── Unauthorized ──
  if (!isAuthorized) {
    return (
      <div style={PAGE}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "80vh", padding: 32 }}>
          <div style={{ width: 60, height: 60, borderRadius: 18, backgroundColor: "rgba(255,149,0,.12)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
            <ShieldExclamation size={28} color="#FF9500" />
          </div>
          <h3 style={{ fontSize: "1.125rem", fontWeight: 700, color: "#1c1c1e", marginBottom: 6, textAlign: "center" }}>Access Restricted</h3>
          <p style={{ fontSize: "0.875rem", color: "rgba(60,60,67,.5)", textAlign: "center", marginBottom: 24 }}>Merchant authentication required.</p>
          <button onClick={logout} style={{ padding: "11px 28px", borderRadius: 12, border: "none", backgroundColor: "#007AFF", color: "#fff", fontSize: "0.9375rem", fontWeight: 600, cursor: "pointer", fontFamily: FONT }}>
            Sign In as Merchant
          </button>
        </div>
      </div>
    );
  }

  // ── Product data loading ──
  if (state.loading) {
    return (
      <div style={PAGE}>
        <div className="pp-container" style={{ padding: "28px 16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
            <Skel w={34} h={34} r={17} /><Skel w={150} h={22} r={7} />
          </div>
          <div className="pp-main-grid" style={{ marginBottom: 16 }}>
            <Skel w="100%" h={280} r={16} />
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <Skel w="100%" h={140} r={16} />
              <Skel w="100%" h={100} r={16} />
              <Skel w="100%" h={72} r={16} />
            </div>
          </div>
          <Skel w="100%" h={180} r={16} />
        </div>
      </div>
    );
  }

  // ── Error ──
  if (state.error) {
    return (
      <div style={PAGE}>
        <div className="pp-container" style={{ padding: "28px 16px" }}>
          <div style={{ backgroundColor: "rgba(255,59,48,.08)", borderRadius: 12, padding: "14px 16px", fontSize: "0.875rem", color: "#FF3B30" }}>
            {state.error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={PAGE}>
      <Seo {...generateNoIndexSEO("Product")} />
      <div className="pp-container">

        {/* Back nav */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
          <button onClick={() => navigate(-1)} style={{ width: 34, height: 34, borderRadius: "50%", background: "rgba(60,60,67,.08)", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#1c1c1e", flexShrink: 0 }}>
            <ArrowLeft size={16} />
          </button>
          <h2 style={{ fontSize: "1.375rem", fontWeight: 700, color: "#1c1c1e", letterSpacing: "-0.03em", margin: 0 }}>Product Details</h2>
        </div>

        {/* Image + Details grid */}
        <div className="pp-main-grid">

          {/* Image card */}
          <div style={{ ...card, padding: 0, overflow: "hidden" }}>
            <img
              src={state.product?.imageSrc}
              alt={state.product?.name}
              className="pp-image"
            />
          </div>

          {/* Details column */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

            {/* Header card: ID, name, price, edit button */}
            <div style={card}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 4 }}>
                <div>
                  <span style={{ fontSize: "0.72rem", fontWeight: 500, color: "rgba(60,60,67,.38)", display: "block", marginBottom: 6, fontVariantNumeric: "tabular-nums" }}>
                    #{state.product?.id}
                  </span>
                  <h2 style={{ fontSize: "1.25rem", fontWeight: 700, color: "#1c1c1e", margin: "0 0 8px", letterSpacing: "-0.025em", lineHeight: 1.25 }}>
                    {state.product?.name}
                  </h2>
                  <span style={{ fontSize: "1.125rem", fontWeight: 700, color: "#34C759", letterSpacing: "-0.02em" }}>
                    {formatCurrency(state.product?.price)}
                  </span>
                </div>
                <button
                  className="pp-icon-btn"
                  onClick={handleProductEditRedirect}
                  style={{ width: 34, height: 34, borderRadius: "50%", background: "rgba(0,122,255,.08)", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#007AFF", flexShrink: 0 }}
                >
                  <Pencil size={15} />
                </button>
              </div>
            </div>

            {/* Description card */}
            <div style={card}>
              <div style={{ fontSize: "0.72rem", fontWeight: 600, color: "rgba(60,60,67,.42)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
                Description
              </div>
              <p style={{ fontSize: "0.9375rem", color: "rgba(60,60,67,.7)", lineHeight: 1.55, margin: 0 }}>
                {state.product?.description}
              </p>
            </div>

            {/* Category / Type cards */}
            <div className={`pp-cat-type-grid${state.product?.type ? " pp-two" : ""}`}>
              <div style={card}>
                <div style={{ fontSize: "0.72rem", fontWeight: 600, color: "rgba(60,60,67,.42)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
                  Category
                </div>
                <p style={{ fontSize: "0.9375rem", color: "#1c1c1e", fontWeight: 500, margin: 0 }}>
                  {state.product?.category}
                </p>
              </div>
              {state.product?.type && (
                <div style={card}>
                  <div style={{ fontSize: "0.72rem", fontWeight: 600, color: "rgba(60,60,67,.42)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
                    Type
                  </div>
                  <p style={{ fontSize: "0.9375rem", color: "#1c1c1e", fontWeight: 500, margin: 0 }}>
                    {state.product?.type}
                    {state.product?.subType && (
                      <span style={{ color: "rgba(60,60,67,.4)", fontWeight: 400 }}> · {state.product.subType}</span>
                    )}
                  </p>
                </div>
              )}
            </div>

          </div>
        </div>

        {/* Options section */}
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <h3 style={{ fontSize: "1.0625rem", fontWeight: 600, color: "#1c1c1e", letterSpacing: "-0.02em", margin: 0 }}>Options</h3>
            <button
              onClick={handleAddBtnClick}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "8px 16px", borderRadius: 20, border: "none",
                backgroundColor: "#007AFF", color: "#fff",
                fontSize: "0.8125rem", fontWeight: 600,
                cursor: "pointer", fontFamily: FONT,
              }}
            >
              + Add option
            </button>
          </div>

          {state.options.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {state.options.map((op) => (
                <div key={op?.id} style={card}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 2 }}>
                    <h4 style={{ fontSize: "0.9375rem", fontWeight: 600, color: "#1c1c1e", margin: 0, letterSpacing: "-0.015em" }}>
                      {op?.title}
                    </h4>
                    <button
                      className="pp-icon-btn"
                      onClick={(e) => handleOptionEditRedirect(e, op.id, op.name, op.title, op.required)}
                      style={{ width: 30, height: 30, borderRadius: "50%", background: "rgba(0,122,255,.08)", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#007AFF", flexShrink: 0 }}
                    >
                      <Pencil size={13} />
                    </button>
                  </div>
                  <OpList id={op.id} />
                </div>
              ))}
            </div>
          ) : (
            <div style={{ ...card, textAlign: "center", padding: "40px 20px" }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
                <BoxSeam size={28} color="rgba(60,60,67,.28)" />
                <p style={{ fontSize: "0.9375rem", color: "rgba(60,60,67,.45)", margin: 0 }}>
                  No options yet
                </p>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default Product;
